/**
 * AiAutocomplete — Tiptap extension that suggests an inline continuation of
 * the current paragraph as ghost text (grey, non-editable, never touches the
 * document) while the user writes.
 *
 * Flow:
 *  1. On doc/selection change (with the setting enabled), a debounce timer
 *     is armed. Autocomplete only triggers when the selection is empty, the
 *     document has content, and the cursor sits at the end of a text block.
 *  2. After the debounce, `api.autocomplete` is called with the trailing
 *     context. An AbortController tracks the in-flight request so a newer
 *     keystroke/request can mark it obsolete — its result is then ignored.
 *     While a request is actually in flight, `onPendingChange(true)` fires
 *     so the host can show a subtle "thinking" indicator near the caret.
 *  3. The suggestion is rendered via a ProseMirror widget Decoration (no doc
 *     mutation). Tab accepts it (inserts the text); Escape or any further
 *     typing/selection change discards it.
 *
 * Error handling: network/timeout errors are swallowed (best-effort — never
 * blocks the writer or future attempts). A quota-exceeded response
 * (`AiLimitError`) is different: it's reported once per editor instance via
 * `onQuotaExceeded` so the host can show a single, non-spammy warning instead
 * of silently going dark.
 *
 * Settings (enabled/length) are pushed in imperatively via the
 * `setAiAutocompleteSettings` command — mirrors the `setCodexEntries` pattern
 * used by CodexHighlight, so toggling the toolbar switch doesn't require
 * recreating the editor.
 *
 * Priority: set high (1000) so this extension's Tab/Escape handling always
 * takes precedence over other keymaps registered later in resolution order
 * (e.g. list-item indentation) whenever a suggestion is visible.
 */

import { Extension } from '@tiptap/core';
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import type { EditorState, Transaction } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { Node as PmNode } from '@tiptap/pm/model';
import { api, AiLimitError } from '../../lib/api';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type AutocompleteLength = 'short' | 'long';

export interface AiAutocompleteOptions {
  projectId: string;
  chapterId: string;
  enabled: boolean;
  length: AutocompleteLength;
  /** Fired whenever the visible suggestion changes — lets the host React
   *  component render a "Tab to accept" affordance near the caret. */
  onSuggestionChange?: (suggestion: string | null, from: number | null) => void;
  /** Fired once per editor instance the first time a request comes back with
   *  an exhausted-quota error, so the host can show a single, non-spammy
   *  warning instead of the writer silently getting no suggestions. */
  onQuotaExceeded?: () => void;
  /** Fired true right before an autocomplete request goes out, and false as
   *  soon as it settles (success, error, or is aborted by a newer keystroke).
   *  Lets the host render a subtle "thinking" indicator near the caret. */
  onPendingChange?: (pending: boolean) => void;
}

// ---------------------------------------------------------------------------
// Plugin state
// ---------------------------------------------------------------------------

interface AutocompletePluginState {
  enabled: boolean;
  length: AutocompleteLength;
  suggestion: string | null;
  /** Doc position right where the ghost text should render (== cursor at request time) */
  from: number | null;
  decorations: DecorationSet;
}

type AutocompleteMeta =
  | { type: 'settings'; enabled: boolean; length: AutocompleteLength }
  | { type: 'suggestion'; suggestion: string; from: number }
  | { type: 'clear' };

const AUTOCOMPLETE_KEY = new PluginKey<AutocompletePluginState>('aiAutocomplete');

const DEBOUNCE_MS = 600;
const CONTEXT_CHARS = 1500;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Render the suggestion as a dim, non-editable widget right after the cursor.
 *  TAPPING it accepts the suggestion — the hidden mobile affordance (no pill,
 *  nothing over the text). Desktop still has Tab. */
function buildGhostDecoration(doc: PmNode, pos: number, text: string): DecorationSet {
  if (!text) return DecorationSet.empty;
  const widget = Decoration.widget(
    pos,
    (view) => {
      const span = document.createElement('span');
      span.textContent = text;
      span.setAttribute('data-ai-ghost', 'true');
      span.setAttribute('aria-hidden', 'true');
      // Inherit the surrounding text color (works in both paper/night themes) and
      // dim it. Tappable to accept (cursor:pointer); no animation.
      span.style.cssText =
        'color:inherit;opacity:.42;font-style:italic;cursor:pointer;user-select:none;white-space:pre-wrap;';
      span.addEventListener('pointerdown', (e) => {
        e.preventDefault(); // don't move the caret / lose the suggestion
        const tr = view.state.tr.insertText(text, pos);
        // Keep the caret at the END of the accepted text (like normal typing) —
        // without this the selection can map to a wrong spot and "jump up".
        tr.setSelection(TextSelection.create(tr.doc, pos + text.length));
        tr.setMeta(AUTOCOMPLETE_KEY, { type: 'clear' } satisfies AutocompleteMeta);
        view.dispatch(tr);
        view.focus();
      });
      return span;
    },
    { side: 1, ignoreSelection: true },
  );
  return DecorationSet.create(doc, [widget]);
}

// ---------------------------------------------------------------------------
// Command type augmentation
// ---------------------------------------------------------------------------

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    aiAutocomplete: {
      /** Insert the current ghost suggestion into the document (Tab). */
      acceptAiSuggestion: () => ReturnType;
      /** Discard the current ghost suggestion without inserting it (Esc). */
      dismissAiSuggestion: () => ReturnType;
      /** Push the latest settings (toggle + length) without recreating the editor. */
      setAiAutocompleteSettings: (opts: { enabled: boolean; length: AutocompleteLength }) => ReturnType;
    };
  }
}

// ---------------------------------------------------------------------------
// Extension
// ---------------------------------------------------------------------------

export const AiAutocomplete = Extension.create<AiAutocompleteOptions>({
  name: 'aiAutocomplete',

  // High priority: guarantees Tab-to-accept wins over other keymaps (e.g.
  // list-item indentation) whenever a ghost suggestion is on screen.
  priority: 1000,

  addOptions() {
    return {
      projectId: '',
      chapterId: '',
      enabled: false,
      length: 'short',
      onSuggestionChange: undefined,
      onQuotaExceeded: undefined,
      onPendingChange: undefined,
    };
  },

  addCommands() {
    return {
      acceptAiSuggestion:
        () =>
        ({ state, dispatch, tr }) => {
          const pState = AUTOCOMPLETE_KEY.getState(state);
          if (!pState?.suggestion || pState.from == null) return false;
          if (dispatch) {
            const end = pState.from + pState.suggestion.length;
            tr.insertText(pState.suggestion, pState.from);
            tr.setSelection(TextSelection.create(tr.doc, end));
            tr.setMeta(AUTOCOMPLETE_KEY, { type: 'clear' } satisfies AutocompleteMeta);
            dispatch(tr);
          }
          return true;
        },
      dismissAiSuggestion:
        () =>
        ({ state, dispatch, tr }) => {
          const pState = AUTOCOMPLETE_KEY.getState(state);
          if (!pState?.suggestion) return false;
          if (dispatch) {
            tr.setMeta(AUTOCOMPLETE_KEY, { type: 'clear' } satisfies AutocompleteMeta);
            dispatch(tr);
          }
          return true;
        },
      setAiAutocompleteSettings:
        (opts) =>
        ({ dispatch, tr }) => {
          if (dispatch) {
            tr.setMeta(AUTOCOMPLETE_KEY, {
              type: 'settings',
              enabled: opts.enabled,
              length: opts.length,
            } satisfies AutocompleteMeta);
            dispatch(tr);
          }
          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    const options = this.options;

    return [
      new Plugin<AutocompletePluginState>({
        key: AUTOCOMPLETE_KEY,

        state: {
          init(): AutocompletePluginState {
            return {
              enabled: options.enabled,
              length: options.length,
              suggestion: null,
              from: null,
              decorations: DecorationSet.empty,
            };
          },

          apply(tr: Transaction, old: AutocompletePluginState): AutocompletePluginState {
            const meta = tr.getMeta(AUTOCOMPLETE_KEY) as AutocompleteMeta | undefined;

            if (meta?.type === 'settings') {
              if (!meta.enabled) {
                return {
                  ...old,
                  enabled: meta.enabled,
                  length: meta.length,
                  suggestion: null,
                  from: null,
                  decorations: DecorationSet.empty,
                };
              }
              return { ...old, enabled: meta.enabled, length: meta.length };
            }

            if (meta?.type === 'suggestion') {
              if (!old.enabled) return old;
              const sel = tr.selection;
              // Cursor must still be exactly where the request was made from —
              // otherwise the suggestion is stale and gets dropped silently.
              if (!sel.empty || sel.head !== meta.from) {
                return { ...old, suggestion: null, from: null, decorations: DecorationSet.empty };
              }
              return {
                ...old,
                suggestion: meta.suggestion,
                from: meta.from,
                decorations: buildGhostDecoration(tr.doc, meta.from, meta.suggestion),
              };
            }

            if (meta?.type === 'clear') {
              return { ...old, suggestion: null, from: null, decorations: DecorationSet.empty };
            }

            // Regular transaction (typing, selection move, etc.) — any doc change
            // or cursor move away from the anchor discards the current suggestion.
            if (old.suggestion !== null) {
              const stillValid = !tr.docChanged && tr.selection.empty && tr.selection.head === old.from;
              if (!stillValid) {
                return { ...old, suggestion: null, from: null, decorations: DecorationSet.empty };
              }
              return { ...old, decorations: old.decorations.map(tr.mapping, tr.doc) };
            }

            return old;
          },
        },

        props: {
          decorations(state: EditorState): DecorationSet | null {
            return AUTOCOMPLETE_KEY.getState(state)?.decorations ?? null;
          },

          handleKeyDown(view, event) {
            const pState = AUTOCOMPLETE_KEY.getState(view.state);
            if (!pState?.suggestion || pState.from == null) return false;

            if (event.key === 'Tab') {
              event.preventDefault();
              const end = pState.from + pState.suggestion.length;
              const tr = view.state.tr.insertText(pState.suggestion, pState.from);
              tr.setSelection(TextSelection.create(tr.doc, end));
              tr.setMeta(AUTOCOMPLETE_KEY, { type: 'clear' } satisfies AutocompleteMeta);
              view.dispatch(tr);
              return true;
            }

            if (event.key === 'Escape') {
              event.preventDefault();
              view.dispatch(view.state.tr.setMeta(AUTOCOMPLETE_KEY, { type: 'clear' } satisfies AutocompleteMeta));
              return true;
            }

            return false;
          },
        },

        view(editorView) {
          let debounceTimer: ReturnType<typeof setTimeout> | null = null;
          let controller: AbortController | null = null;
          let destroyed = false;
          let lastNotifiedSuggestion: string | null = null;
          let lastNotifiedFrom: number | null = null;
          // A quota warning is surfaced at most once per editor instance —
          // re-typing/re-triggering never re-spams the toast.
          let quotaWarned = false;
          // Tracks whether the host was last told "pending: true", so we only
          // ever emit a matching "pending: false" (no redundant callbacks).
          let pendingActive = false;

          const setPending = (pending: boolean) => {
            if (pending === pendingActive) return;
            pendingActive = pending;
            options.onPendingChange?.(pending);
          };

          const cancelPending = () => {
            if (debounceTimer) {
              clearTimeout(debounceTimer);
              debounceTimer = null;
            }
            controller?.abort();
            controller = null;
            setPending(false);
          };

          return {
            update(view, prevState) {
              const pState = AUTOCOMPLETE_KEY.getState(view.state);
              if (!pState) return;

              if (pState.suggestion !== lastNotifiedSuggestion || pState.from !== lastNotifiedFrom) {
                lastNotifiedSuggestion = pState.suggestion;
                lastNotifiedFrom = pState.from;
                options.onSuggestionChange?.(pState.suggestion, pState.from);
              }

              if (!pState.enabled) {
                cancelPending();
                return;
              }

              const docChanged = !view.state.doc.eq(prevState.doc);
              const selChanged = !view.state.selection.eq(prevState.selection);
              // Turning the toggle ON suggests IMMEDIATELY (don't make the writer
              // type more just to see it work) — treat the disabled→enabled
              // transition like a fresh trigger at the current cursor.
              const prevEnabled = AUTOCOMPLETE_KEY.getState(prevState)?.enabled ?? false;
              const justEnabled = !prevEnabled && pState.enabled;
              if (!docChanged && !selChanged && !justEnabled) return;

              // Fresh input invalidates anything scheduled/in-flight.
              cancelPending();

              const { selection } = view.state;
              if (!selection.empty) return; // never suggest over a range selection
              if (view.state.doc.textContent.trim().length === 0) return; // empty doc

              const $from = selection.$from;
              const atBlockEnd = $from.parentOffset === $from.parent.content.size;
              if (!atBlockEnd) return; // only at end of a paragraph/line

              const pos = selection.from;
              const length = pState.length;

              debounceTimer = setTimeout(() => {
                debounceTimer = null;
                const ac = new AbortController();
                controller = ac;
                setPending(true);

                const before = view.state.doc.textBetween(
                  Math.max(0, pos - CONTEXT_CHARS),
                  pos,
                  '\n\n',
                  '\n\n',
                );

                api
                  .autocomplete({ projectId: options.projectId, chapterId: options.chapterId, before, length })
                  .then((res) => {
                    if (destroyed || ac.signal.aborted) return; // cancelPending() already cleared "pending"
                    setPending(false);
                    const cur = view.state.selection;
                    if (!cur.empty || cur.from !== pos) return; // cursor moved — stale
                    const suggestion = res.suggestion?.trim();
                    if (!suggestion) return;
                    const tr = view.state.tr.setMeta(AUTOCOMPLETE_KEY, {
                      type: 'suggestion',
                      suggestion,
                      from: pos,
                    } satisfies AutocompleteMeta);
                    view.dispatch(tr);
                  })
                  .catch((err: unknown) => {
                    if (destroyed || ac.signal.aborted) return; // cancelPending() already cleared "pending"
                    setPending(false);
                    // Quota errors are worth telling the writer about (once) —
                    // everything else (network hiccups, timeouts) stays silent
                    // and never blocks the next attempt.
                    if (err instanceof AiLimitError && !quotaWarned) {
                      quotaWarned = true;
                      options.onQuotaExceeded?.();
                    }
                  });
              }, DEBOUNCE_MS);
            },

            destroy() {
              destroyed = true;
              cancelPending();
            },
          };
        },
      }),
    ];
  },
});
